import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Icon from '../components/common/Icon';
import Breadcrumbs from '../components/common/Breadcrumbs';
import SectionHeader from '../components/common/SectionHeader';
import NewsCard from '../components/common/NewsCard';
import EventCard from '../components/common/EventCard';
import CircleCard from '../components/common/CircleCard';
import Toolbar from '../components/common/Toolbar';
import SearchBar from '../components/common/SearchBar';
import FilterSelect from '../components/common/FilterSelect';
import StatsStrip from '../components/common/StatsStrip';

const fedData = {
  roma: {
    title: 'Federazione Roma',
    type: 'Federazione Provinciale',
    heroImage: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?q=80&w=1600&auto=format&fit=crop',
    head: { 
      name: 'Alessia Turri', 
      role: 'Segretaria Provinciale', 
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=400&auto=format&fit=crop',
      bio: "Guida la federazione più grande d'Italia con visione e determinazione. Alessia lavora per unire i territori e dare forza alle battaglie generazionali nella Capitale."
    },
    description: "La federazione metropolitana di Roma coordina oltre 40 circoli, rappresentando il cuore pulsante dell'attività politica dei Giovani Democratici nel Lazio.",
    color: '#e11d48', // Un rosso leggermente diverso o il primario
    icon: 'Building2',
    stats: [
      { number: '40', label: 'Circoli' },
      { number: '800+', label: 'Iscritti' },
      { number: '15', label: 'Municipi' }
    ],
    circoli: [
      { id: 1, name: 'GD Trastevere', location: 'I Municipio', image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?q=80&w=600&auto=format&fit=crop' },
      { id: 2, name: 'GD Pigneto', location: 'V Municipio', image: 'https://images.unsplash.com/photo-1520175480921-4edfa0683a2f?q=80&w=600&auto=format&fit=crop' },
      { id: 3, name: 'GD Testaccio', location: 'I Municipio', image: 'https://images.unsplash.com/photo-1512411516428-21f440536484?q=80&w=600&auto=format&fit=crop' }
    ],
    news: [
      { id: 1, title: 'Conferenza Programmatica Roma 2030', date: '20 Apr 2026', category: 'Politica', excerpt: 'Le proposte dei GD Roma per il futuro della città metropolitana: trasporti, ambiente e lavoro.', image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=600' },
      { id: 2, title: 'Mobilitazione Nazionale: Roma c\'è', date: '12 Apr 2026', category: 'Eventi', excerpt: 'Grande partecipazione della federazione romana alla manifestazione per il salario minimo.', image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?q=80&w=600' }
    ],
    events: [
      { id: 101, title: 'Assemblea Provinciale', day: '10', month: 'Giu', location: 'Roma, Largo del Nazareno', time: '17:00', badge: 'Assemblea', image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=600' }
    ],
    documents: [
      { title: 'Regolamento Federazione Roma', type: 'Statuto', date: '01 Mar 2026', badge: 'PDF', size: '1.8 MB' },
      { title: 'Bilancio Sociale 2025', type: 'Report', date: '15 Gen 2026', badge: 'PDF', size: '3.2 MB' }
    ]
  }
};

const FederazionePage = () => {
  const { id } = useParams();
  const data = fedData[id] || fedData.roma;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Federazioni', path: '/federazioni-provinciali' },
    { label: data.title }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <Layout>
      {/* EPIC HERO SECTION */}
      <section className="hero--epic">
        <div className="hero__bg">
          {data.heroImage ? (
            <div className="hero__image" style={{ backgroundImage: `url(${data.heroImage})` }}></div>
          ) : (
            <div className="hero__image" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, #450a0a 100%)' }}></div>
          )}
          <div className="hero__overlay" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(9,9,11,0.95) 100%)` }}></div>
        </div>
        
        <div className="hero__content-epic container" style={{ color: '#fff' }}>
          <div className="reveal" style={{ marginBottom: 'var(--space-8)' }}>
            <Breadcrumbs items={breadcrumbs} style={{ color: 'rgba(255,255,255,0.7)' }} />
          </div>
          
          <div className="reveal mb-6">
            <span className="badge" style={{ background: data.color, color: '#fff', border: 'none', padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)' }}>
              {data.type}
            </span>
          </div>
          
          <h1 className="hero__title-epic text-reveal-trigger" style={{ color: '#fff', fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
            <span className="line text-reveal"><span className="word text-reveal__inner">{data.title}</span></span>
          </h1>
          
          <p className="hero__subtitle-epic reveal" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '700px', fontSize: 'var(--text-body-lg)' }}>
            {data.description}
          </p>

          <div className="reveal mt-10">
            <StatsStrip stats={data.stats} style={{ background: 'transparent', border: 'none', padding: 0, justifyContent: 'flex-start', gap: 'var(--space-12)' }} />
          </div>
        </div>
      </section>

      {/* SEGRETARIA & INFO SECTION */}
      <section className="section" style={{ marginTop: '-100px', position: 'relative', zIndex: 10, paddingTop: 0 }}>
        <div className="container">
          <div className="reveal reveal--scale mb-12">
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.8)', 
              backdropFilter: 'blur(20px)',
              padding: 'var(--space-8)', 
              borderRadius: 'var(--radius-3xl)',
              boxShadow: '0 30px 60px rgba(0,0,0,0.12)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--space-10)'
            }}>
              <div style={{ width: '180px', height: '180px', borderRadius: 'var(--radius-2xl)', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
                <img src={data.head.image} alt={data.head.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <span className="text-overline mb-2 block" style={{ color: data.color }}>{data.head.role}</span>
                <h2 className="h2 mb-4">{data.head.name}</h2>
                <p className="text-body text-secondary mb-6 italic" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  "{data.head.bio}"
                </p>
                <div className="flex gap-4">
                  <Link to="/contatti" className="btn btn--primary">Contatta la Federazione</Link>
                  <div className="flex gap-3 ml-4 items-center">
                    <Link to="#" className="text-secondary hover:text-primary transition-colors"><Icon name="Mail" size={20} /></Link>
                    <Link to="#" className="text-secondary hover:text-primary transition-colors"><Icon name="Twitter" size={20} /></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TERRITORIAL NETWORK */}
          <div style={{ background: 'var(--color-bg-subtle)', padding: 'var(--space-10)', borderRadius: 'var(--radius-3xl)', border: '1px solid var(--color-border-subtle)' }} className="reveal reveal--scale">
            <div className="flex justify-between items-end mb-10">
              <SectionHeader overline="La nostra rete" title="Circoli della Federazione" className="mb-0" />
              <Link to="/circoli" className="btn btn--outline btn--sm">Vedi tutti i circoli →</Link>
            </div>
            <div className="grid grid-3 gap-6">
              {data.circoli.map((circolo, i) => (
                <CircleCard 
                  key={circolo.id} 
                  name={circolo.name} 
                  location={circolo.location} 
                  image={circolo.image}
                  className={`reveal stagger-${i + 1}`} 
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NEWS SECTION */}
      <section className="section bg-subtle">
        <div className="container">
          <SectionHeader overline="Hub Provinciale" title="Ultime dalla Federazione" className="mb-8" />
          
          <Toolbar
            className="mb-10 reveal"
            left={<SearchBar placeholder="Cerca tra le notizie..." ariaLabel="Cerca notizie federazione" />}
            right={<FilterSelect label="Categoria" options={['Tutte', 'Politica', 'Eventi', 'Comunicati']} />}
          />

          <div className="grid grid-3 gap-8">
            {data.news.map((n, i) => (
              <NewsCard 
                key={n.id} 
                news={n} 
                title={n.title}
                excerpt={n.excerpt}
                badge={n.category}
                meta={[n.date]}
                image={n.image}
                className={`reveal stagger-${i + 1}`} 
              />
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS SECTION */}
      <section className="section bg-white border-bottom">
        <div className="container">
          <SectionHeader overline="Agenda Provinciale" title="Appuntamenti e Mobilitazioni" className="mb-8" />
          
          <Toolbar className="mb-8 reveal"
            left={<FilterSelect label="Tipo" options={['Tutti', 'Assemblea', 'Manifestazione', 'Formazione']} />}
            right={<button className="btn btn--outline btn--sm">📅 Calendario Federazione</button>}
          />

          <div className="grid grid-3 gap-8">
            {data.events.map((e, i) => (
              <EventCard 
                key={e.id} 
                {...e}
                className={`reveal stagger-${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS SECTION */}
      <section className="section bg-subtle">
        <div className="container">
          <SectionHeader overline="Risorse" title="Documenti Ufficiali" className="mb-8" />
          
          <Toolbar className="mb-8 reveal"
            left={<SearchBar placeholder="Cerca risorsa..." ariaLabel="Cerca" />}
          />

          <div className="flex flex-col gap-4 reveal">
            {data.documents.map((d, i) => (
              <a 
                href="#" 
                className={`card flex justify-between items-center hover:border-primary transition-all stagger-${i + 1}`} 
                style={{ padding: 'var(--space-6)', background: '#fff', border: '1px solid var(--color-border-subtle)', borderRadius: 'var(--radius-xl)' }} 
                key={d.title}
              >
                <div style={{ flex: 1 }}>
                  <span className="badge badge--subtle mb-2" style={{ color: data.color, background: `${data.color}10` }}>{d.type}</span>
                  <h4 className="h5 mb-1">{d.title}</h4>
                  <span className="text-xs text-secondary uppercase tracking-wider">{d.date} • {d.size}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="badge badge--primary" style={{ background: data.color }}>{d.badge}</span>
                  <div className="btn btn--icon btn--sm btn--outline"><Icon name="Download" size={16} /></div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default FederazionePage;
